import * as graph from '@adc/sdk'
import analytics from '@adc/analytics'
import instrument from '@adc/instrument'
import {
  mapPinTrackingType,
  getMapPinTrackingInfo,
} from '../components/marker/helpers'

const { eventNames, track } = analytics

const SUCCESS_STATUS = 'success'
const ASSET_INTERACTION_METRIC_NAME = 'srp_asset_action'
export const ONE_CLICK_SAVE_SEARCH_START = eventNames.oneClickSaveSearchStart
export const ONE_CLICK_SAVE_SEARCH_SUCCESS =
  eventNames.oneClickSaveSearchSuccess
export const ONE_CLICK_SAVE_SEARCH_UPDATE_NAME =
  eventNames.oneClickSaveSearchUpdateName
export const ONE_CLICK_SAVE_SEARCH_REMOVE = eventNames.oneClickSaveSearchRemove
export const ONE_CLICK_SAVE_SEARCH_CLOSE = eventNames.oneClickSaveSearchClose
export const MOBILE_LISTING_CARD = 'mobile_listing_card'
export const LISTING_CARD = 'listing_card'

const defaultEmptyValue = 'none'
const defaultBadgesList = [defaultEmptyValue]

const mapTriggerToVersion = {
  asset_row: 'v1',
  asset_tile: 'v1',
  listing_card: 'v2',
  mobile_listing_card: 'v2',
  search_results_list: 'v1',
}

const getAnalyticsBadgeInfo = badges => {
  return {
    propertyBadges: badges.map(badge => badge.replace(/\s/g, '')).join(),
  }
}

const instrumentAssetInteraction = ({
  interactionName,
  triggeredFrom,
  isReserveMetShown,
  displayedCurrentBid,
}) => {
  instrument.increment(ASSET_INTERACTION_METRIC_NAME, 1, [
    `asset_interaction_name:${interactionName}`,
    `version:${mapTriggerToVersion[triggeredFrom]}`,
    `trigger:${triggeredFrom}`,
    `is_reserve_met_shown:${Boolean(isReserveMetShown)}`,
    `is_current_bid_shown:${Boolean(displayedCurrentBid)}`,
  ])
}

const trackPropertyClick = ({
  listing,
  triggeredFrom,
  badges = defaultBadgesList,
  isReserveMetShown = false,
  displayedCurrentBid,
  displayedERV,
}) => {
  instrument.domainEvent({
    type: graph.enums.DomainEventTypeName.ListingCardClicked,
    source: listing.urn,
    data: {
      triggered_from: triggeredFrom,
      is_reserve_met_shown: isReserveMetShown,
      is_current_bid_shown: displayedCurrentBid,
      is_erv_shown: displayedERV,
    },
  })
  instrumentAssetInteraction({
    interactionName: 'select',
    triggeredFrom,
    isReserveMetShown,
    displayedCurrentBid,
  })

  const eventName =
    triggeredFrom === MOBILE_LISTING_CARD
      ? eventNames.srpPropertyClickMobile
      : eventNames.srpPropertyClick
  return track(eventName, {
    status: SUCCESS_STATUS,
    ...analytics.standardTracking.listing(listing),
    ...getAnalyticsBadgeInfo(badges),
    currentBid: displayedCurrentBid || defaultEmptyValue,
    erv: displayedERV || defaultEmptyValue,
  })
}

const trackPropertySave = ({
  listing,
  triggeredFrom,
  isSaved,
  badges = defaultBadgesList,
  isReserveMetShown,
  displayedCurrentBid,
  displayedERV,
}) => {
  instrumentAssetInteraction({
    interactionName: isSaved ? 'unsave' : 'save',
    triggeredFrom,
    isReserveMetShown,
    isCurrentBidShown: Boolean(displayedCurrentBid),
  })

  return track(eventNames.srpPropertySave, {
    status: SUCCESS_STATUS,
    action: isSaved ? 'remove' : 'save',
    ...analytics.standardTracking.listing(listing),
    ...getAnalyticsBadgeInfo(badges),
    currentBid: displayedCurrentBid || defaultEmptyValue,
    erv: displayedERV || defaultEmptyValue,
  })
}

export const trackSaveSearch = ({ event, user }) => {
  return track(event, {
    status: SUCCESS_STATUS,
    ...analytics.standardTracking.user(user),
  })
}

const getMapPinAdditionalTrackingObject = (listing, featureFlags = {}) => {
  let mapPinType = mapPinTrackingType.ORIGINAL
  const currentMapPinActiveTrackingVariant = getMapPinTrackingInfo(
    listing,
    featureFlags
  )
  if (currentMapPinActiveTrackingVariant[mapPinTrackingType.UPDATED]) {
    mapPinType = mapPinTrackingType.UPDATED
  } else if (currentMapPinActiveTrackingVariant[mapPinTrackingType.NEW]) {
    mapPinType = mapPinTrackingType.NEW
  } else if (currentMapPinActiveTrackingVariant[mapPinTrackingType.SEEN]) {
    mapPinType = mapPinTrackingType.SEEN
  }
  return {
    mapPinType,
    mapPinSeen: getMapPinTrackingInfo(listing, featureFlags)[
      mapPinTrackingType.SEEN
    ],
  }
}

const trackPageLoadWithFlags = (user = {}, featureFlags = {}) => {
  // This condition is used to determine which SEARCH button combination was
  // displayed on page load

  const additionalData = {
    testId3: '30002',
    testVariant3: 'ss2',
    ...(featureFlags['fe.resi.ab.testing.onboarding_drawer'] &&
      featureFlags['fe.resi.ab.testing.onboarding_drawer']?.test),
    ...(featureFlags['fe.resi.ab.testing.signup-propositions'] &&
      featureFlags['fe.resi.ab.testing.signup-propositions']?.test),
    ...(featureFlags['fe.resi.ab.testing.onboarding_drawer_content'] &&
      featureFlags['fe.resi.ab.testing.onboarding_drawer_content']?.test),
    ...(featureFlags['fe.resi.ab.testing.srp.mobile.popular.filters'] &&
      featureFlags['fe.resi.ab.testing.srp.mobile.popular.filters']?.test),
    ...(featureFlags['fe.resi.enabled-photo-enhancement'] &&
      featureFlags['fe.resi.enabled-photo-enhancement']?.test),
    ...(featureFlags['fe.resi.ab.testing.signup.thank-you-page'] &&
      featureFlags['fe.resi.ab.testing.signup.thank-you-page']?.test),
    ...(featureFlags?.['fe.resi.ab.testing.onboarding-drawer-group'] || {}),
  }

  return trackPageLoad(user, additionalData)
}

const trackPageLoad = (user = {}, additionalData) => {
  analytics.init(window.CONFIGS).then(() => {
    analytics.trackDataLayerLoaded(user, {
      ...additionalData,
      status: SUCCESS_STATUS,
    })
  })
}

// to be fired when using ajax search
// virtualTitle: <location><tps/reo/all><online/in person>
const trackVirtualSearchResults = (
  newSearchReqUrl,
  numberSearchResults,
  virtualTitle
) => {
  const virtualSearchResultsPage = {
    status: SUCCESS_STATUS,
    newSearch: newSearchReqUrl,
    numberSearchResults,
    virtualTitle,
    virtualUrl: window.location.pathname,
  }

  track(eventNames.virtualPageview, virtualSearchResultsPage)
}

// virtualTitle: <location><tps/reo/all><online/in person>
const getVirtualTitle = filterData => {
  let assetType = 'all'
  let assetLocation = 'all'
  const locationParts = []
  // location search
  if (filterData.property_city) locationParts.push(filterData.property_city)
  if (filterData.property_county) locationParts.push(filterData.property_county)
  if (filterData.property_state) locationParts.push(filterData.property_state)
  if (filterData.property_zip) locationParts.push(filterData.property_zip)
  const location = locationParts.join(' ')
  // asset type
  const isReoSearch =
    filterData.usecode_product_type &&
    filterData.usecode_product_type.indexOf('resi_new_foreclosed') >= 0
  const isTpsSearch =
    filterData.usecode_product_type &&
    filterData.usecode_product_type.indexOf('resi_ft') >= 0
  if (isReoSearch && !isTpsSearch) assetType = 'reo'
  if (isTpsSearch && !isReoSearch) assetType = 'tps'
  // asset location
  if (
    Object.prototype.hasOwnProperty.call(filterData, 'is_asset_online_event')
  ) {
    assetLocation =
      filterData.is_asset_online_event === true ? 'online' : 'in person' // eslint-disable-line
  }
  return `${location}/${assetType}/${assetLocation}`
}

const trackMapPin = ({
  listing = {},
  isMobileView,
  isCurrentBidShown,
  isReserveMetShown = false,
  featureFlags = {},
  isHoverEvent = false,
}) => {
  instrument.increment(ASSET_INTERACTION_METRIC_NAME, 1, [
    `asset_interaction_name:${
      isMobileView ? 'mobile-map-card-open' : 'select'
    }`,
    `trigger:map_marker`,
    `is_mobile:${Boolean(isMobileView)}`,
    `version:v2`,
    `is_current_bid_shown:${Boolean(isCurrentBidShown)}`,
    `is_reserve_met_shown:${Boolean(isReserveMetShown)}`,
  ])
  let eventName = eventNames.mapClick
  if (isMobileView) {
    eventName = eventNames.mapClickMobile
  } else if (isHoverEvent) {
    // only send hover event if we're not mobile and isHoverEvent is true
    eventName = eventNames.mapPinHover
  }
  return track(eventName, {
    status: SUCCESS_STATUS,
    ...analytics.standardTracking.listing(listing),
    ...getMapPinAdditionalTrackingObject(listing, featureFlags),
  })
}

export {
  getVirtualTitle,
  trackVirtualSearchResults,
  trackPageLoadWithFlags,
  trackMapPin,
  trackPropertyClick,
  trackPropertySave,
}
